import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';
import { handleThunkApiError } from '../../utils/handleApiError';

// Create new lead with client info
export const createLead = createAsyncThunk(
  'leads/createLead',
  async (leadData, { rejectWithValue }) => {
    try {
      // Check if clientInfo contains just an ID (selected existing client)
      const clientData = leadData.clientInfo?._id ? 
        { client: leadData.clientInfo._id } : 
        {
          client: {
            name: leadData.clientInfo.name,
            email: leadData.clientInfo.email,
            cellPhone: leadData.clientInfo.cellPhone,
            altPhone: leadData.clientInfo.altPhone,
            address: {
              street: leadData.clientInfo.address,
              city: leadData.clientInfo.city,
              state: leadData.clientInfo.state,
              zipCode: leadData.clientInfo.zipCode
            }
          }
        };

      // Debug the billing contact structure
      console.log('leadData structure:', JSON.stringify(leadData, null, 2));
      console.log('billingContact structure:', JSON.stringify(leadData.billingContact, null, 2));

      // Transform the form data to match the backend structure
      const transformedData = {
        leadContact: {
          name: leadData.leadContact.name,
          phone: leadData.leadContact.phone,
          email: leadData.leadContact.email,
          notes: leadData.leadContact.notes
        },
        address: {
          city: leadData.address?.city || ''
        },
        projectManagers: leadData.projectManagers,
        ...clientData,
        siteContact: {
          name: leadData.siteContact.name,
          email: leadData.siteContact.email,
          cellPhone: leadData.siteContact.cellPhone,
          altPhone: leadData.siteContact.altPhone,
          address: {
            street: leadData.siteContact.address,
            city: leadData.siteContact.city,
            state: leadData.siteContact.state,
            zipCode: leadData.siteContact.zipCode
          }
        },
        // Add billing contact information
        billingContact: leadData.billingContact
      };

      // Validate required fields
      if (!transformedData.leadContact?.name) {
        return rejectWithValue('Contact name is required');
      }

      if (!transformedData.leadContact?.email) {
        return rejectWithValue('Contact email is required');
      }

      if (!transformedData.address?.city) {
        return rejectWithValue('City is required');
      }

      if (!transformedData.projectManagers?.length) {
        return rejectWithValue('At least one project manager is required');
      }

      console.log('Sending lead data:', transformedData);

      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(transformedData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to create lead');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Upload attachments
export const uploadAttachments = createAsyncThunk(
  'leads/uploadAttachments',
  async ({ leadId, files }, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // Add progress tracking
      let uploadProgress = 0;
      const progressIncrement = 100 / files.length;
      
      // Add all files to the FormData
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${config.apiUrl}/api/leads/${leadId}/attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to upload attachments');
      }

      // Set progress to 100% when complete
      dispatch(setUploadProgress(100));
      
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new action for fetching leads with pagination
export const fetchLeads = createAsyncThunk(
  'leads/fetchLeads',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const { 
        page = 1, 
        limit = 10, 
        sortField, 
        sortOrder, 
        search,
        isArchived,
        trackingStatus,
        leadStatus,
        priority,
        projectManagerId 
      } = params;
      
      const token = localStorage.getItem('token');
      
      const queryParams = new URLSearchParams({
        page,
        limit,
        ...(sortField && { sortField }),
        ...(sortOrder && { sortOrder }),
        ...(search && { search }),
        ...(typeof isArchived === 'boolean' && { isArchived: isArchived.toString() }),
        ...(trackingStatus && trackingStatus !== 'all' && { trackingStatus }),
        ...(leadStatus && leadStatus !== 'all' && { leadStatus }),
        ...(priority && { priority }),
        ...(projectManagerId && { projectManagerId }),
        ...(params.startDate && { startDate: params.startDate }),
        ...(params.endDate && { endDate: params.endDate })
      }).toString();

      // Log the final URL for debugging
      console.log(`Fetching leads with URL: ${config.apiUrl}/api/leads?${queryParams}`);

      const response = await fetch(`${config.apiUrl}/api/leads?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        return handleThunkApiError(response, rejectWithValue, { dispatch });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'An error occurred');
    }
  }
);

// Add new action for loading preferences
export const loadColumnPreferences = createAsyncThunk(
  'leads/loadColumnPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.apiUrl}/api/users/preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to load preferences');
      }

      // Return the preferences object
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Update the saveColumnPreferences action
export const saveColumnPreferences = createAsyncThunk(
  'leads/saveColumnPreferences',
  async ({ columns, type }, { rejectWithValue }) => {  // Update to receive object parameter
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/users/preferences/columns`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ columns, type }) // Send both columns and type
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to save column preferences');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new action for deleting leads
export const deleteLead = createAsyncThunk(
  'leads/deleteLead',
  async (leadId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/leads/${leadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete lead');
      }

      return { leadId, message: data.message };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Update the updateLead action
export const updateLead = createAsyncThunk(
  'leads/updateLead',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      
      // Pass through the updates directly since they're already in the correct format
      const response = await fetch(`${config.apiUrl}/api/leads/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update lead');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Fetch a single lead with its full details (including attachments, notes and
// categoryId, which are omitted from the leads list response for performance).
export const fetchLeadById = createAsyncThunk(
  'leads/fetchLeadById',
  async (leadId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/leads/${leadId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch lead');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Upload attachments for a lead
export const uploadLeadAttachments = createAsyncThunk(
  'leads/uploadLeadAttachments',
  async ({ leadId, files }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${config.apiUrl}/api/leads/${leadId}/attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to upload attachments');
      }

      // Return the full lead object that contains the attachments
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new action creator
export const deleteLeadAttachment = createAsyncThunk(
  'leads/deleteLeadAttachment',
  async ({ leadId, attachmentId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/leads/${leadId}/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete attachment');
      }

      return { ...data, leadId };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new action to fetch categories
export const fetchCategories = createAsyncThunk(
  'leads/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch categories');
      }

      return data.categories;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add validation helper
const validateProposalData = (proposalData) => {
  const errors = [];
  
  if (!proposalData.emailAddress) {
    errors.push('Client email address is required');
  }
  
  if (!proposalData.category) {
    errors.push('Category is required');
  }
  
  return errors;
};

// Update the createProposal action
export const createProposal = createAsyncThunk(
  'leads/createProposal',
  async ({ leadId, proposalData }, { rejectWithValue }) => {
    try {
      // Validate proposal data
      const validationErrors = validateProposalData(proposalData);
      if (validationErrors.length > 0) {
        return rejectWithValue(validationErrors.join(', '));
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/leads/${leadId}/proposals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(proposalData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to create proposal');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

const leadsSlice = createSlice({
  name: 'leads',
  initialState: {
    leads: [],
    currentLead: null,
    categories: [],
    loading: false,
    error: null,
    uploadProgress: 0,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      pages: 0
    },
    columnPreferences: []
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        state.loading = false;
        state.leads.push(action.payload.lead);
        state.currentLead = action.payload.lead;
        showToast('Lead created successfully', 'success');
      })
      .addCase(createLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(uploadAttachments.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadAttachments.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadProgress = 100;
        state.currentLead = {
          ...state.currentLead,
          attachments: action.payload.attachments
        };
        showToast('Files uploaded successfully', 'success');
      })
      .addCase(uploadAttachments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.uploadProgress = 0;
        showToast(action.payload, 'error');
      })
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload.leads;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(loadColumnPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadColumnPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.columnPreferences = action.payload.preferences?.leadColumns || [];
      })
      .addCase(loadColumnPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(saveColumnPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveColumnPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.columnPreferences = action.payload.preferences?.leadColumns || [];
        showToast('Column preferences saved successfully', 'success');
      })
      .addCase(saveColumnPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(deleteLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLead.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = state.leads.filter(lead => lead._id !== action.payload.leadId);
        showToast('Lead deleted successfully', 'success');
      })
      .addCase(deleteLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(updateLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        state.loading = false;
        // Update the lead in the state
        const index = state.leads.findIndex(lead => lead._id === action.payload.lead._id);
        if (index !== -1) {
          state.leads[index] = action.payload.lead;
        }
        showToast('Lead updated successfully', 'success');
      })
      .addCase(updateLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(uploadLeadAttachments.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadLeadAttachments.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadProgress = 100;
        
        // Update the current lead with the new data
        if (state.currentLead && state.currentLead._id === action.payload.lead._id) {
          state.currentLead = action.payload.lead;
        }
        
        // Update the lead in the leads array if it exists
        const leadIndex = state.leads.findIndex(lead => lead._id === action.payload.lead._id);
        if (leadIndex !== -1) {
          state.leads[leadIndex] = action.payload.lead;
        }
        
        showToast('Files uploaded successfully', 'success');
      })
      .addCase(uploadLeadAttachments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.uploadProgress = 0;
        showToast(action.payload, 'error');
      })
      .addCase(deleteLeadAttachment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLeadAttachment.fulfilled, (state, action) => {
        state.loading = false;
        // Update the lead's attachments in the state
        const leadIndex = state.leads.findIndex(lead => lead._id === action.payload.leadId);
        if (leadIndex !== -1) {
          state.leads[leadIndex].attachments = action.payload.attachments;
        }
        showToast('Attachment deleted successfully', 'success');
      })
      .addCase(deleteLeadAttachment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, setUploadProgress } = leadsSlice.actions;
export default leadsSlice.reducer; 